import type { FC } from 'react'
import styled from '@emotion/styled'

import sanitizeHtml from 'sanitize-html'

import Typography from '@segment/matcha/components/Typography'
import Divider from '@segment/matcha/components/Divider'
import Image from '@segment/matcha/components/Image'

import type { PageConfig } from '@segment/contexts/page'
import { usePageConfig } from '@segment/contexts/page'

import introductionPng2x from './assets/Introduction@2x.png'
import introductionWebp from './assets/Introduction.webp'
import introductionWebp2x from './assets/Introduction@2x.webp'

const getCopy = ({ baseUrl, basePath }: PageConfig) => ({
  introduction: [
    `As we step into 2024, it's evident that customer experiences have become the ultimate differentiator in a fiercely competitive market. At the same time, Artificial Intelligence (AI) and machine learning (ML) have emerged as pivotal forces, revolutionizing the way businesses interact with their customers.`,
    `This paradigm shift has led to the evolution of the traditional tech stack into something more dynamic and customer-centric: what we like to call the Customer Engagement Stack.`,
    `Central to this new stack is the Customer Data Platform (CDP), a tool that unifies data to fuel unparalleled customer experiences. However, the journey towards efficient and fully integrated customer data infrastructure is fraught with challenges. Recent research shows that almost <a href="https://www.salesforce.com/news/stories/data-skills-research/">one-third (30%) of business leaders</a> are overwhelmed by the amount of data across their organizations, which is expected to more than double in size by 2026.`,
    `In our comprehensive <a href="${baseUrl}${basePath}/the-cdp-report">2024 CDP Report</a>, we explored over 12 Trillion API Calls to provide an updated perspective on the technology ecosystems that shape customer interactions. Based on our findings, we've identified the top categories and the most impactful applications that are now critical components of the modern Customer Engagement Stack.`,
    `As you navigate this guide, you’ll learn how the right tools and tactics, with a CDP as your cornerstone, can revolutionize your organization. Embracing this data-driven approach will pave the way for better customer connections, accelerate business growth, and solidify your position as a market leader.`,
  ],
})
const Introduction: FC = () => {
  const pageConfig = usePageConfig()

  return (
    <Wrapper id="introduction">
      <Title variant="h2" tag="h2">
        Introduction
      </Title>
      <StyledDivider />
      <CopyWrapper>
        {getCopy(pageConfig).introduction.map((paragraph) => (
          <ContentParagraph
            key={paragraph}
            tag="span"
            variant="pLarge"
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(paragraph, { allowedTags: [`a`] }),
            }}
          />
        ))}
      </CopyWrapper>
      <Picture>
        <source type="image/webp" srcSet={`${introductionWebp}, ${introductionWebp2x} 2x`} />
        <Image width="860" height="700" src={introductionPng2x} alt="Illustration: Introduction" loading="lazy" />
      </Picture>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  max-width: 844px;
  padding-top: 60px;
  margin: 0 24px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    margin: 0 auto;
  }
`

const StyledDivider = styled(Divider)`
  margin-bottom: 40px;
`

const CopyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`

const ContentParagraph = styled(Typography)`
  margin: 0 auto;
  color: ${({ theme }) => theme.palette.twilio.gray[100]};
`

const Title = styled(Typography)`
  color: ${({ theme }) => theme.palette.twilio.gray[60]};
  margin-bottom: 20px;
`

const Picture = styled.picture`
  margin-top: 40px;
  display: block;
`
export default Introduction
